import { FormTypes } from '@oneblink/types'
import { FormElement } from '@oneblink/types/typescript/forms'

interface HasChildren {
  elements?: FormElement[]
}

export function insertAfter(
  elements: FormTypes.FormElement[],
  targetElement: FormTypes.FormElement,
  insertElement: FormTypes.FormElement,
) {
  const newEls = []
  for (let i = 0; i < elements.length; i++) {
    newEls.push(elements[i])
    if (elements[i] === targetElement) {
      newEls.push(insertElement)
    }
  }

  return newEls
}

export function replaceElement<InsertElement extends FormTypes.FormElement>(
  elements: FormTypes.FormElement[],
  targetElement: FormTypes.FormElement,
  insertElement: InsertElement,
) {
  return elements.reduce((memo: FormElement[], el) => {
    if (el.id === targetElement.id) memo.push(insertElement)
    else memo.push(el)

    return memo
  }, [])
}

export function removeElement(
  elements: FormTypes.FormElement[],
  targetElement: FormTypes.FormElement,
) {
  return elements.reduce((memo: FormElement[], el) => {
    if (el.id !== targetElement.id) memo.push(el)

    return memo
  }, [])
}

export function findElement<T extends FormTypes.FormElement>(
  parentElement: HasChildren,
  elementNameToFind: string,
): {
  parent: HasChildren
  value: T
} | void {
  for (const iterElement of parentElement.elements || []) {
    switch (iterElement.type) {
      case 'form':
      case 'repeatableSet':
      case 'page':
      case 'infoPage':
      case 'section':
        if ('name' in iterElement && iterElement.name === elementNameToFind)
          return { parent: parentElement, value: iterElement as T }
        const result = findElement<T>(
          iterElement as HasChildren,
          elementNameToFind,
        )
        if (result) return result
        break
      default:
        if (iterElement.name === elementNameToFind) {
          return { parent: parentElement, value: iterElement as T }
        }
    }
  }
}

export function findPage(definition: FormTypes.Form, pageLabel: string) {
  const pageElement = definition.elements.find(
    (element) => element.type === 'page' && element.label === pageLabel,
  )

  if (pageElement) {
    return pageElement as FormTypes.PageElement
  }
}

export function findSubForm(
  parentElement: HasChildren,
  formNameToFind: string,
): {
  parent: HasChildren
  value: FormTypes.FormFormElement
} | void {
  for (const iterElement of parentElement.elements || []) {
    if (iterElement.type === 'form' && iterElement.name === formNameToFind) {
      return { parent: parentElement, value: iterElement }
    }
    switch (iterElement.type) {
      case 'form':
      case 'repeatableSet':
      case 'page':
      case 'infoPage':
      case 'section':
        const result = findSubForm(iterElement as HasChildren, formNameToFind)
        if (result) return result
        break
    }
  }
}

export function findTag(formDefinition: FormTypes.Form, tagName: string) {
  return formDefinition.tags.find(
    (tag) => tag.toLowerCase() === tagName.toLowerCase(),
  )
}

export function createElementOptions(
  options: Array<string | undefined>,
): FormTypes.ChoiceElementOption[] {
  const filteredOptions = options.filter((o) => !!o) as string[]
  // Ids must be derived from the option. `useFormElementOptions` matches
  // conditionally shown options by id, so a new id for an unchanged option
  // looks like the option disappeared and the value gets cleared.
  return filteredOptions.map((opt) => ({
    id: opt,
    label: opt,
    value: opt,
  }))
}

export function replaceElementOptions(
  definition: HasChildren,
  elementName: string,
  options: FormTypes.ChoiceElementOption[],
  otherValues: string[] = ['Other'],
) {
  const optionSet = [...options].filter((o) => !!o.value)
  const element = findElement<FormTypes.FormElementWithOptions>(
    definition,
    elementName,
  )

  if (
    element &&
    element.value &&
    element.value.options &&
    element.parent &&
    element.parent.elements
  ) {
    const otherOptions = otherValues.reduce(
      (memo: FormTypes.ChoiceElementOption[], value) => {
        const option = element.value.options?.find(
          (opt) =>
            !!opt.value && opt.value.toLowerCase() === value.toLowerCase(),
        )
        if (option) {
          memo.push(option)
        }
        return memo
      },
      [],
    )
    if (otherOptions) {
      optionSet.push(...otherOptions)
    }

    // Replacing the element with an identical set of options would hand the
    // renderer a new element identity for no reason, which re-runs the effects
    // keyed on it. Leave the definition untouched when nothing has changed.
    if (isSameOptionSet(element.value.options, optionSet)) {
      return
    }

    element.parent.elements = replaceElement(
      element.parent.elements,
      element.value,
      {
        ...element.value,
        options: optionSet,
      },
    )
  }
}

function isSameOptionSet(
  current: FormTypes.ChoiceElementOption[],
  next: FormTypes.ChoiceElementOption[],
) {
  return (
    current.length === next.length &&
    current.every(
      (option, index) =>
        option.id === next[index].id &&
        option.label === next[index].label &&
        option.value === next[index].value,
    )
  )
}
